/// Zustand store for the authenticated user + their Firestore profile.
/// React components subscribe to render the signed-in badge, the
/// lastViewedLevel highlight, the per-level best scores, etc.
///
/// Boot flow lives in `bootProfile()`: anonymous Firebase Auth ➜
/// load-or-create the Firestore doc ➜ store on success. Components
/// gate their render on `state === 'ready'`.

import { create } from 'zustand'
import type { User } from 'firebase/auth'
import {
  authProviderOf,
  describeEmailCollision,
  ensureSignedIn,
  isEmailCollisionError,
  onUserChange,
  sendPasswordReset as sendPasswordResetAuth,
  signInWithApple as signInWithAppleAuth,
  signInWithEmail as signInWithEmailAuth,
  signInWithGoogle as signInWithGoogleAuth,
  signOutToAnonymous,
  signUpWithEmail as signUpWithEmailAuth,
} from '../auth/auth'
import {
  loadOrCreateProfile,
  recordDailyCompletion,
  recordLevelEnd as recordLevelEndDb,
  setAuthProvider as setAuthProviderDb,
  setLastViewedLevel as setLastViewedLevelDb,
  type DifficultyName,
  type LevelStats,
  type UserProfile,
} from '../data/userProfile'
import { LEVEL_REGISTRY } from '../data/loadLevel'
import { dailyDayIndex, dailyLevelId, isoDayString } from '../data/towerHome'
import { incrementAndFetchPlayCount } from '../data/playCount'
import {
  startPresence,
  subscribePlayingNowBreakdown,
  type PlayingNowBreakdown,
} from '../data/presence'
import {
  leaderboardDisplayName,
  publishLeaderboardEntry,
  publishDailyLeaderboardEntry,
} from '../data/leaderboard'
import { logSignIn, logSignUp } from '../analytics/AnalyticsManager'

export type ProfileState =
  | { kind: 'idle' }
  | { kind: 'loading' }
  | { kind: 'ready'; user: User; profile: UserProfile }
  | { kind: 'error'; message: string }

export type SignInResult =
  | { ok: true }
  | { ok: false; reason: 'cancelled' | 'in_use' | 'unsupported' | 'unknown'; message: string }

export type EmailSignUpResult =
  | { ok: true; verificationSent: boolean }
  | {
      ok: false
      reason: 'weak-password' | 'email-in-use' | 'invalid-email' | 'unsupported' | 'unknown'
      message: string
    }

export type EmailSignInResult =
  | { ok: true }
  | {
      ok: false
      reason: 'invalid-credentials' | 'invalid-email' | 'too-many-requests' | 'unsupported' | 'unknown'
      message: string
    }

export type PasswordResetResult =
  | { ok: true }
  | {
      ok: false
      reason: 'invalid-email' | 'unsupported' | 'unknown'
      message: string
    }

interface ProfileStore {
  state: ProfileState
  /// Total website visits across all users, fetched once per page
  /// load via `incrementWebPlayCount`. `null` until the call lands;
  /// chrome shows nothing while loading.
  playCount: number | null
  /// Live presence: total online + per-level breakdown. `null`
  /// until the first subscription emits. The total counts every
  /// online user (including those on the home screen); `byLevel`
  /// only contains entries for users whose `levelId` is set
  /// (i.e., inside a GameView).
  playingNow: PlayingNowBreakdown | null
  boot(defaultLastViewedLevel: string): Promise<void>
  setLastViewedLevel(levelId: string): Promise<void>
  recordLevelEnd(args: {
    levelId: string
    difficulty: DifficultyName
    succeeded: boolean
    score: number
    successCount: number
    totalHandled: number
  }): Promise<LevelStats | null>
  /// Trigger the Google OAuth flow. Resolves with `ok: true` on
  /// success (state already updated to the upgraded user) or a
  /// classified failure with a human-readable message the chrome
  /// can surface as a toast / inline error.
  signInWithGoogle(): Promise<SignInResult>
  /// Trigger the Apple OAuth flow. Same flow + result shape as
  /// signInWithGoogle: anonymous → Apple upgrade in place where
  /// possible, displaces to an existing Apple-linked UID otherwise.
  signInWithApple(): Promise<SignInResult>
  /// Create an email/password account, linking from the current
  /// anonymous user where possible. A verification email is sent
  /// automatically; the chrome can prompt the user to check it.
  signUpWithEmail(args: {
    email: string
    password: string
    displayName?: string
  }): Promise<EmailSignUpResult>
  /// Sign in with email/password. Replaces the current user with
  /// the email-authenticated one — anonymous progress is left
  /// behind under its old UID.
  signInWithEmail(args: { email: string; password: string }): Promise<EmailSignInResult>
  /// Send a password-reset email. Resolves with ok: true on
  /// success or a classified failure.
  sendPasswordReset(email: string): Promise<PasswordResetResult>
  /// Sign out of the current account and re-anonymise so the
  /// home screen never shows a no-user state. Existing Firestore
  /// docs stay put under their original UID.
  signOut(): Promise<void>
}

export const useProfileStore = create<ProfileStore>((set, get) => ({
  state: { kind: 'idle' },
  playCount: null,
  playingNow: null,

  async boot(defaultLastViewedLevel: string) {
    if (get().state.kind === 'loading' || get().state.kind === 'ready') return
    set({ state: { kind: 'loading' } })
    try {
      const user = await ensureSignedIn()
      const profile = await loadOrCreateProfile({
        uid: user.uid,
        authProvider: authProviderOf(user),
        defaultLastViewedLevel,
      })
      set({ state: { kind: 'ready', user, profile } })
      // Bump the global play counter once per boot. Fire-and-forget
      // — failure (App Check, function cold-start error, offline)
      // just means the chrome stays without the count this visit.
      incrementAndFetchPlayCount()
        .then((playCount) => set({ playCount }))
        .catch(() => {
          /* swallowed; non-essential UI affordance */
        })
      // Mark this client present in RTDB and start streaming the
      // live "playing now" count. Both subscriptions stay open for
      // the lifetime of the page; RTDB's `onDisconnect` cleans up
      // our presence entry server-side when the tab closes.
      startPresence(user.uid)
      subscribePlayingNowBreakdown((playingNow) => set({ playingNow }))
      // Keep the store in sync if the auth state ever changes
      // (sign-out, link-to-Apple/Google upgrade, etc.).
      onUserChange((u) => {
        if (!u) {
          // Firebase signed us out (e.g., session expired). Drop
          // back to idle so the boot flow restarts.
          set({ state: { kind: 'idle' } })
          return
        }
        const cur = get().state
        if (cur.kind === 'ready' && cur.user.uid === u.uid) {
          // Same UID — could be a profile-data refresh after the
          // anonymous→Google upgrade. Update the User reference so
          // displayName/email reflect on the chrome immediately.
          set({ state: { ...cur, user: u } })
        }
      })
    } catch (e) {
      const message = e instanceof Error ? e.message : String(e)
      set({ state: { kind: 'error', message } })
    }
  },

  async signInWithGoogle() {
    const cur = get().state
    if (cur.kind !== 'ready') {
      return { ok: false, reason: 'unknown', message: 'Sign-in not ready yet' }
    }
    try {
      const result = await signInWithGoogleAuth()
      const user = result.user
      const provider = authProviderOf(user)
      const sameUid = user.uid === cur.user.uid
      if (sameUid) {
        // In-place upgrade. Keep the existing profile in memory,
        // just stamp the new provider.
        try {
          await setAuthProviderDb(user.uid, provider)
        } catch (e) {
          console.warn('setAuthProvider failed (non-fatal):', e)
        }
        set({
          state: {
            kind: 'ready',
            user,
            profile: { ...cur.profile, authProvider: provider, lastSeenAt: Date.now() },
          },
        })
      } else {
        // UID changed — the freshly-created anonymous user was
        // displaced because this Google account is already linked
        // to an existing Firebase UID. Load the profile for that
        // existing UID; the abandoned anonymous Firestore doc
        // (always empty in this scenario, since the player just
        // arrived) is left in place — pruning is out of scope.
        const profile = await loadOrCreateProfile({
          uid: user.uid,
          authProvider: provider,
          defaultLastViewedLevel: cur.profile.lastViewedLevel,
        })
        set({ state: { kind: 'ready', user, profile } })
      }
      logSignIn('google')
      return { ok: true }
    } catch (e) {
      const code = (e as { code?: string }).code ?? ''
      if (code === 'auth/popup-closed-by-user' || code === 'auth/cancelled-popup-request') {
        return { ok: false, reason: 'cancelled', message: 'Sign-in cancelled' }
      }
      if (code === 'auth/operation-not-allowed') {
        return {
          ok: false,
          reason: 'unsupported',
          message: 'Google sign-in is not enabled for this site yet.',
        }
      }
      if (isEmailCollisionError(e)) {
        const message = await describeEmailCollision(e)
        return { ok: false, reason: 'in_use', message }
      }
      const message = e instanceof Error ? e.message : String(e)
      console.warn('Google sign-in failed:', e)
      return { ok: false, reason: 'unknown', message }
    }
  },

  async signInWithApple() {
    const cur = get().state
    if (cur.kind !== 'ready') {
      return { ok: false, reason: 'unknown', message: 'Sign-in not ready yet' }
    }
    try {
      const result = await signInWithAppleAuth()
      const user = result.user
      const provider = authProviderOf(user)
      const sameUid = user.uid === cur.user.uid
      if (sameUid) {
        try {
          await setAuthProviderDb(user.uid, provider)
        } catch (e) {
          console.warn('setAuthProvider failed (non-fatal):', e)
        }
        set({
          state: {
            kind: 'ready',
            user,
            profile: { ...cur.profile, authProvider: provider, lastSeenAt: Date.now() },
          },
        })
      } else {
        const profile = await loadOrCreateProfile({
          uid: user.uid,
          authProvider: provider,
          defaultLastViewedLevel: cur.profile.lastViewedLevel,
        })
        set({ state: { kind: 'ready', user, profile } })
      }
      logSignIn('apple')
      return { ok: true }
    } catch (e) {
      const code = (e as { code?: string }).code ?? ''
      if (code === 'auth/popup-closed-by-user' || code === 'auth/cancelled-popup-request') {
        return { ok: false, reason: 'cancelled', message: 'Sign-in cancelled' }
      }
      if (code === 'auth/operation-not-allowed') {
        return {
          ok: false,
          reason: 'unsupported',
          message: 'Apple sign-in is not enabled for this site yet.',
        }
      }
      if (isEmailCollisionError(e)) {
        const message = await describeEmailCollision(e)
        return { ok: false, reason: 'in_use', message }
      }
      const message = e instanceof Error ? e.message : String(e)
      console.warn('Apple sign-in failed:', e)
      return { ok: false, reason: 'unknown', message }
    }
  },

  async signUpWithEmail(args) {
    const cur = get().state
    if (cur.kind !== 'ready') {
      return { ok: false, reason: 'unknown', message: 'Sign-up not ready yet' }
    }
    try {
      const result = await signUpWithEmailAuth(args)
      const user = result.user
      const provider = authProviderOf(user)
      const sameUid = user.uid === cur.user.uid
      if (sameUid) {
        try {
          await setAuthProviderDb(user.uid, provider)
        } catch (e) {
          console.warn('setAuthProvider failed (non-fatal):', e)
        }
        set({
          state: {
            kind: 'ready',
            user,
            profile: { ...cur.profile, authProvider: provider, lastSeenAt: Date.now() },
          },
        })
      } else {
        const profile = await loadOrCreateProfile({
          uid: user.uid,
          authProvider: provider,
          defaultLastViewedLevel: cur.profile.lastViewedLevel,
        })
        set({ state: { kind: 'ready', user, profile } })
      }
      logSignUp('email')
      return { ok: true, verificationSent: result.verificationSent }
    } catch (e) {
      const code = (e as { code?: string }).code ?? ''
      if (code === 'auth/weak-password') {
        return {
          ok: false,
          reason: 'weak-password',
          message: 'Password must be at least 6 characters.',
        }
      }
      if (code === 'auth/email-already-in-use') {
        return {
          ok: false,
          reason: 'email-in-use',
          message: 'That email is already registered. Try signing in instead.',
        }
      }
      if (code === 'auth/invalid-email') {
        return { ok: false, reason: 'invalid-email', message: 'Email format is invalid.' }
      }
      if (code === 'auth/operation-not-allowed') {
        return {
          ok: false,
          reason: 'unsupported',
          message: 'Email/password sign-in is not enabled for this site yet.',
        }
      }
      const message = e instanceof Error ? e.message : String(e)
      console.warn('Email sign-up failed:', e)
      return { ok: false, reason: 'unknown', message }
    }
  },

  async signInWithEmail(args) {
    const cur = get().state
    if (cur.kind !== 'ready') {
      return { ok: false, reason: 'unknown', message: 'Sign-in not ready yet' }
    }
    try {
      const user = await signInWithEmailAuth(args)
      const provider = authProviderOf(user)
      const sameUid = user.uid === cur.user.uid
      if (sameUid) {
        set({
          state: {
            kind: 'ready',
            user,
            profile: { ...cur.profile, authProvider: provider, lastSeenAt: Date.now() },
          },
        })
      } else {
        const profile = await loadOrCreateProfile({
          uid: user.uid,
          authProvider: provider,
          defaultLastViewedLevel: cur.profile.lastViewedLevel,
        })
        set({ state: { kind: 'ready', user, profile } })
      }
      logSignIn('email')
      return { ok: true }
    } catch (e) {
      const code = (e as { code?: string }).code ?? ''
      if (
        code === 'auth/invalid-credential' ||
        code === 'auth/wrong-password' ||
        code === 'auth/user-not-found'
      ) {
        return {
          ok: false,
          reason: 'invalid-credentials',
          message: 'Email or password is incorrect.',
        }
      }
      if (code === 'auth/invalid-email') {
        return { ok: false, reason: 'invalid-email', message: 'Email format is invalid.' }
      }
      if (code === 'auth/too-many-requests') {
        return {
          ok: false,
          reason: 'too-many-requests',
          message: 'Too many attempts. Try again in a few minutes or reset your password.',
        }
      }
      if (code === 'auth/operation-not-allowed') {
        return {
          ok: false,
          reason: 'unsupported',
          message: 'Email/password sign-in is not enabled for this site yet.',
        }
      }
      const message = e instanceof Error ? e.message : String(e)
      console.warn('Email sign-in failed:', e)
      return { ok: false, reason: 'unknown', message }
    }
  },

  async sendPasswordReset(email) {
    try {
      await sendPasswordResetAuth(email)
      return { ok: true }
    } catch (e) {
      const code = (e as { code?: string }).code ?? ''
      if (code === 'auth/invalid-email') {
        return { ok: false, reason: 'invalid-email', message: 'Email format is invalid.' }
      }
      if (code === 'auth/operation-not-allowed') {
        return {
          ok: false,
          reason: 'unsupported',
          message: 'Password reset is not enabled for this site yet.',
        }
      }
      const message = e instanceof Error ? e.message : String(e)
      console.warn('Password reset failed:', e)
      return { ok: false, reason: 'unknown', message }
    }
  },

  async signOut() {
    try {
      // Capture the pre-signout lastViewed so the new anonymous
      // session opens on the same world-map pin if it has one.
      const prev = get().state
      const lastViewed =
        prev.kind === 'ready' ? prev.profile.lastViewedLevel : 'TUTORIAL'
      await signOutToAnonymous()
      // Reset to idle so boot()'s guard lets it run again, then
      // boot the fresh anonymous user.
      set({ state: { kind: 'idle' } })
      await get().boot(lastViewed)
    } catch (e) {
      console.warn('signOut failed:', e)
    }
  },

  async setLastViewedLevel(levelId: string) {
    const s = get().state
    if (s.kind !== 'ready') return
    if (s.profile.lastViewedLevel === levelId) return
    try {
      await setLastViewedLevelDb(s.user.uid, levelId)
      set({
        state: {
          ...s,
          profile: { ...s.profile, lastViewedLevel: levelId, lastSeenAt: Date.now() },
        },
      })
    } catch (e) {
      console.warn('setLastViewedLevel failed', e)
    }
  },

  async recordLevelEnd(args) {
    const s = get().state
    if (s.kind !== 'ready') return null
    try {
      const result = await recordLevelEndDb({
        uid: s.user.uid,
        levelId: args.levelId,
        difficulty: args.difficulty,
        succeeded: args.succeeded,
        score: args.score,
        successCount: args.successCount,
        totalHandled: args.totalHandled,
        profile: s.profile,
      })
      const updatedProfile: UserProfile = {
        ...s.profile,
        levelStats: { ...s.profile.levelStats, [args.levelId]: result.stats },
        totalScore: result.totalScore,
        lastSeenAt: Date.now(),
      }
      set({ state: { ...s, profile: updatedProfile } })
      // Best-effort leaderboard publish. Only fires when the run
      // succeeded (Firestore rule requires a `completed` slot to
      // exist for that difficulty). Failure is logged-not-thrown
      // inside the helper, so a leaderboard outage doesn't break
      // the level-end flow.
      if (args.succeeded) {
        void publishLeaderboardEntry({
          uid: s.user.uid,
          displayName: leaderboardDisplayName({
            uid: s.user.uid,
            displayName: s.user.displayName,
            isAnonymous: s.user.isAnonymous,
          }),
          levelId: args.levelId,
          difficulty: args.difficulty,
          profile: updatedProfile,
        })
      }
      // Daily-challenge streak: if the level just finished IS today's daily,
      // advance the streak (showing up counts — not gated on success).
      const day = dailyDayIndex()
      if (args.levelId === dailyLevelId(day, LEVEL_REGISTRY.map((e) => e.id))) {
        const today = isoDayString(day)
        try {
          const daily = await recordDailyCompletion({
            uid: s.user.uid,
            today,
            yesterday: isoDayString(day - 1),
            score: args.score,
            difficulty: args.difficulty,
            profile: updatedProfile,
          })
          set((st) =>
            st.state.kind === 'ready'
              ? { state: { ...st.state, profile: { ...st.state.profile, daily } } }
              : st,
          )
          // Publish today's best daily score to the day-scoped board.
          void publishDailyLeaderboardEntry({
            displayName: leaderboardDisplayName({
              uid: s.user.uid,
              displayName: s.user.displayName,
              isAnonymous: s.user.isAnonymous,
            }),
            date: today,
          })
        } catch (e) {
          console.warn('daily streak update failed', e)
        }
      }
      return result.stats
    } catch (e) {
      console.warn('recordLevelEnd failed', e)
      return null
    }
  },
}))
